import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import {
  EmployeeIdentificationType,
  EmployeeViewModel,
  EmploymentViewModel,
  EmploymentViewModelNew,
} from '@fora-monorepo/employer-my-pages-common/types';
import {
  Assets,
  Box,
  Button,
  ButtonTextOrIcon,
  Flex,
  Headings,
  Link,
  RadioButton,
  SpeechBubble,
  Text,
  useRadioButtonWithValidate,
} from '@foradevelopment/fora-ui';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';

import { useTypedTranslation } from '../../i18n/i18n';
import { links } from '../../router/links';
import { updateEmployment } from '../../redux/employment/employmentThunks';
import { useAppDispatch } from '../../redux/hooks';

export const NEW_EMPLOYMENT = 'NEW_EMPLOYMENT';

export const PartialDuplicateFormTwo = ({
  partialDuplicateOptions,
  partialDuplicateRadioInput,
  setSelectedDuplicateEmployment,
  employmentsLink,
  exactDuplicateOption,
  inputIdType,
  employeeIdInputValue,
}: {
  partialDuplicateOptions: EmploymentViewModelNew[];
  partialDuplicateRadioInput: ReturnType<typeof useRadioButtonWithValidate>;
  setSelectedDuplicateEmployment: Dispatch<
    SetStateAction<{ employee: EmployeeViewModel; isInactive: boolean; policyNumber: string } | null>
  >;
  employmentsLink: string;
  exactDuplicateOption?: EmploymentViewModel;
  inputIdType: EmployeeIdentificationType;
  employeeIdInputValue: string;
}) => {
  const { t } = useTypedTranslation();
  const [addPersonalIdentityNumber, setAddPersonalIdentityNumber] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      setSelectedDuplicateEmployment(null);
      partialDuplicateRadioInput.setValue('');
    };
    // Empty as intended, to make the unmount function only run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = ({
    employee,
    event,
    isInactive,
    policyNumber,
  }: {
    employee: EmployeeViewModel | null;
    event: React.ChangeEvent<HTMLInputElement>;
    isInactive: boolean;
    policyNumber: string;
  }) => {
    if (
      employee?.identificationType === EmployeeIdentificationType.PersonalIdentityNumber ||
      employee?.identificationType === EmployeeIdentificationType.CoOrdinationNumber
    ) {
      setSelectedDuplicateEmployment({ employee, isInactive, policyNumber });
    } else {
      setSelectedDuplicateEmployment(null);
    }
    partialDuplicateRadioInput.onChange(event);
  };

  const handleAddPersonalIdentityNumber = async (employeeIdInputValue: string) => {
    const selectedEmployment = partialDuplicateOptions.find(
      x => x.employee.employeeId === partialDuplicateRadioInput.value
    );

    if (selectedEmployment) {
      setLoadingForm(true);
      const result = await dispatch(
        updateEmployment({
          unchangedEmployment: selectedEmployment,
          updateEmploymentInput: {
            employeeId: employeeIdInputValue,
            partnerNumberIndividual: selectedEmployment.employee.partnerNumberIndividual,
          },
        })
      );
      if (updateEmployment.fulfilled.match(result)) {
        setAddPersonalIdentityNumber(true);
        trackCustomEvent(`Updated coordniation number to personal id numner in duplicate form. `);
      } else if (updateEmployment.rejected.match(result)) {
        trackCustomEvent(
          `Failed to update coordniation number to personal id numner in duplicate form. PartnerNumberIndividual: ${selectedEmployment.employee.partnerNumberIndividual}, agreementNumber: ${selectedEmployment.foraAgreementNumber}`
        );
      }
    }
  };

  return (
    <SpeechBubble marginTop={30} color="babyYellow">
      {exactDuplicateOption && (
        <>
          <Headings.H5>{t('form.handleDuplicate.alreadyRegistredTitle')}</Headings.H5>
          {exactDuplicateOption.isInactive && (
            <>
              <Text marginBottom={20}>{t('form.handleDuplicate.alreadyRegistredInactiveText')}</Text>
              <Link
                styleAs="button"
                to={`${links.addEmploymentPeriod}/${exactDuplicateOption.policyNumber}`}
                hasIcon={false}
              >
                Lägg till ny anställningsperiod
              </Link>
            </>
          )}
          {!exactDuplicateOption.isInactive && (
            <Text marginTop={10}>
              Individen har redan en pågående anställning registrerad på företaget och kan inte läggas till igen.
            </Text>
          )}
        </>
      )}
      {!!partialDuplicateOptions.length && (
        <>
          <Headings.H5>{t('form.handleDuplicate.title')}</Headings.H5>
          <Text marginTop={10}>{t('form.handleDuplicate.description')}</Text>
          <Flex flexDirection="column" rowGap={20} role="radiogroup" marginTop={20}>
            {partialDuplicateOptions?.map(({ employee, policyNumber, isInactive }, i) => {
              const selected = employee.employeeId === partialDuplicateRadioInput.value;
              return (
                <Flex flexDirection="column" key={employee.employeeId}>
                  <RadioButton
                    key={employee.employeeId}
                    id={employee.employeeId}
                    name="duplicate-option"
                    value={employee.employeeId}
                    checked={selected}
                    onChange={event => handleChange({ event, isInactive, employee, policyNumber })}
                    label={t('form.handleDuplicate.label', {
                      employeeName: `${employee.firstName} ${employee.lastName}`,
                      employeeIDType:
                        employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber
                          ? t('common.coordinationNumber')
                          : t('common.personalNumber'),
                      fullBirthDate: `${employee.employeeId.slice(0, -4)}-${employee.employeeId.slice(-4)},`,
                    })}
                  />
                  {selected && isInactive && inputIdType === EmployeeIdentificationType.CoOrdinationNumber && (
                    <>
                      <FeedbackText text={t('form.handleDuplicate.selectedInactiveEmploymentWarning')} />
                      <Box marginLeft={[40, 40, 30]} marginTop={10}>
                        <Link styleAs="button" to={`${links.addEmploymentPeriod}/${policyNumber}`}>
                          {t('section.employeeDetails.addEmplyment')}
                        </Link>
                      </Box>
                    </>
                  )}
                  {selected && !isInactive && (
                    <Box>
                      <FeedbackText text={t('form.handleDuplicate.selectedActiveEmploymentWarning')} />
                      <Box marginLeft={[40, 40, 30]} marginTop={10}>
                        <Link styleAs="link" to={employmentsLink} iconLeft>
                          {t('common.back')}
                        </Link>
                      </Box>
                    </Box>
                  )}
                  {selected &&
                    employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber &&
                    inputIdType === EmployeeIdentificationType.PersonalIdentityNumber && (
                      <Box>
                        <FeedbackText text={t('form.handleDuplicate.selectedHasPin')} />
                        <Box marginLeft={[40, 40, 30]} marginTop={10}>
                          {!addPersonalIdentityNumber && (
                            <Button
                              styleAs="button"
                              onClick={() => handleAddPersonalIdentityNumber(employeeIdInputValue)}
                            >
                              {!loadingForm ? (
                                'Uppdatera med personnummer'
                              ) : (
                                <ButtonTextOrIcon
                                  icon={<Assets.Spinner width={20} color="white" />}
                                  textColor="white"
                                  isLoading={loadingForm}
                                >
                                  Loading
                                </ButtonTextOrIcon>
                              )}
                            </Button>
                          )}
                          {addPersonalIdentityNumber && !isInactive && (
                            <>
                              <Text color="petroleumGreen" marginBottom={10}>
                                <Assets.CheckmarkIcon color="petroleumGreen" marginRight={6} />
                                {t('form.handleDuplicate.didUpdateId')}
                              </Text>
                              <Link to={links.employments} iconLeft>
                                {t('common.back')}
                              </Link>
                            </>
                          )}
                          {addPersonalIdentityNumber && isInactive && (
                            <>
                              <Text color="petroleumGreen" marginBottom={10}>
                                <Assets.CheckmarkIcon color="petroleumGreen" marginRight={6} />
                                {t('form.handleDuplicate.didUpdateId')}
                              </Text>
                              <Link
                                to={`${links.addEmploymentPeriod}/${partialDuplicateOptions[i].policyNumber}`}
                                styleAs="button"
                                hasIcon={false}
                                fullWidth={false}
                              >
                                {t('section.employeeDetails.addEmplyment')}
                              </Link>
                            </>
                          )}
                        </Box>
                      </Box>
                    )}
                </Flex>
              );
            })}
            <RadioButton
              key={NEW_EMPLOYMENT}
              id={NEW_EMPLOYMENT}
              name="new-employment-option"
              value={NEW_EMPLOYMENT}
              checked={NEW_EMPLOYMENT === partialDuplicateRadioInput.value}
              onChange={event => handleChange({ event, employee: null, isInactive: false, policyNumber: '' })}
              label={t('form.handleDuplicate.newEmployment')}
            />
            {partialDuplicateRadioInput.showError && (
              <Flex>
                <Assets.TriangleExclamationIcon marginRight={10} color="burgundy" />
                <Text fontWeight="semibold" color="burgundy">
                  {t('form.handleDuplicate.hasNotMatchedErrorText')}
                </Text>
              </Flex>
            )}
          </Flex>
        </>
      )}
    </SpeechBubble>
  );
};

const FeedbackText = ({ text }: { text: string }) => {
  return (
    <Text color="black70" marginLeft={[40, 40, 30]} marginTop={4}>
      {text}
    </Text>
  );
};
